// src/ui/LoginCard.jsx — email + password sign-in, and the three-step reset behind it.
//
// Deliberately the SAME account flow as the handset (screens/AuthScreen.tsx): the same
// endpoints, the same 8-char/capital/special rules, the same error sentences. Anything
// that diverges here would be a second, subtly different account system.

import React, { useState } from 'react';
import {
  loginWithPassword, sendResetCode, confirmPasswordReset,
  passwordChecks, passwordOk, MSG,
} from '../services/auth';
import { Mail, KeyRound, Eye, EyeOff, Back, Alert, Check, ArrowRight } from './icons';

const Field = ({ icon: Icon, label, hint, ...rest }) => (
  <label className="lg-field">
    <span className="lg-label">{label}</span>
    <span className="lg-input">
      <Icon width={17} height={17} />
      <input {...rest} />
      {hint}
    </span>
  </label>
);

/** The live checklist the app shows under a new password. */
const Rules = ({ pw, confirm }) => {
  const c = passwordChecks(pw, confirm);
  const rows = [
    [c.length, 'At least 8 characters'],
    [c.capital, 'At least one capital letter'],
    [c.special, 'At least one special character (!@#$%^&*…)'],
    [c.match, 'Passwords match'],
  ];
  return (
    <ul className="lg-rules">
      {rows.map(([ok, text]) => (
        <li key={text} className={ok ? 'ok' : ''}>
          <span className="dot">{ok ? <Check width={12} height={12} /> : null}</span>{text}
        </li>
      ))}
    </ul>
  );
};

export default function LoginCard({ onScrollUp, narrow = false }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');

  // reset: 0 = off, 1 = email, 2 = code, 3 = new password
  const [step, setStep] = useState(0);
  const [rEmail, setREmail] = useState('');
  const [rCode, setRCode] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');

  const submitLogin = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr(''); setNote(''); setBusy(true);
    const res = await loginWithPassword(email, password);
    setBusy(false);
    if (!res.ok) { setErr(res.error); return; }
    // 🟢 Nothing else to do here. App.jsx's auth listener takes it from the sign-in and
    //    decides whether this browser still needs approving — that gate is the whole
    //    point, so this component must not act as if the login is finished.
    setPassword('');
  };

  const startReset = () => {
    setStep(1); setErr(''); setNote('');
    setREmail(email); setRCode(''); setPw1(''); setPw2('');
  };
  const leaveReset = () => { setStep(0); setErr(''); setNote(''); };

  const submitReset = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr(''); setNote('');

    if (step === 1) {
      if (!/^\S+@\S+\.\S+$/.test(rEmail.trim())) { setErr(MSG.invalidEmail); return; }
      setBusy(true);
      const res = await sendResetCode(rEmail);
      setBusy(false);
      if (!res.ok) { setErr(res.error); return; }
      setNote(`If an account exists for ${rEmail.trim().toLowerCase()}, a 6-digit code is on its way.`);
      setStep(2);
      return;
    }

    if (step === 2) {
      // 🟥 There is no endpoint that checks a reset code on its own — the server only
      //    validates it while setting the new password. So this step just collects it,
      //    and a wrong code surfaces at step 3, which sends you back here.
      if (rCode.trim().length < 6) { setErr(MSG.codeMissing); return; }
      setStep(3);
      return;
    }

    if (!passwordOk(pw1, pw2)) { setErr(MSG.requirements); return; }
    setBusy(true);
    const res = await confirmPasswordReset(rEmail, rCode, pw1);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error);
      if (/code/i.test(res.error || '')) setStep(2);   // it was the code, not the password
      return;
    }
    setStep(0);
    setEmail(rEmail.trim().toLowerCase());
    setPassword('');
    setNote('Password changed. Log in with your new password.');
  };

  const eye = (
    <button type="button" className="lg-eye" onClick={() => setShow((s) => !s)}
      title={show ? 'Hide password' : 'Show password'} aria-label={show ? 'Hide password' : 'Show password'}>
      {show ? <EyeOff width={17} height={17} /> : <Eye width={17} height={17} />}
    </button>
  );

  if (step === 0) {
    return (
      <form className="pair-card2 lg-card" onSubmit={submitLogin}>
        <h3>Log in with your account</h3>
        <div className="csub">Use the email and password you use on your phone.</div>

        {err && <div className="lg-err"><Alert width={15} height={15} />{err}</div>}
        {note && <div className="lg-note"><Check width={15} height={15} />{note}</div>}

        <Field icon={Mail} label="Email address" type="email" autoComplete="username"
          placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Field icon={KeyRound} label="Password" type={show ? 'text' : 'password'}
          autoComplete="current-password" placeholder="Enter your password" value={password}
          onChange={(e) => setPassword(e.target.value)} hint={eye} />

        <button type="button" className="lg-link right" onClick={startReset}>Forgot password?</button>

        <button className="lg-go" type="submit" disabled={busy}>
          {busy ? 'Signing in…' : <>Log in <ArrowRight width={17} height={17} /></>}
        </button>

        {/* 🟥 THE OFFER HAS TO MATCH THE DEVICE. On a phone the code cannot be
            scanned by the phone showing it, so offering it as an alternative
            way to sign in HERE is a dead end. What that pane is actually for on
            a phone is linking a computer, so that is what it is called. */}
        {/* ⚖️ signing in here is using Allo: the Terms and the Privacy Policy, one click away */}
        <div className="lg-alt lg-legal">
          By logging in you agree to the <a href="/terms" target="_blank" rel="noopener">Terms of Service</a> and
          acknowledge the <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>.
        </div>

        <div className="lg-alt">
          {narrow ? (
            <>
              On a computer?{' '}
              <button type="button" className="lg-link" onClick={onScrollUp}>Link it with a QR code</button>
            </>
          ) : (
            <>
              Rather not type it?{' '}
              <button type="button" className="lg-link" onClick={onScrollUp}>Scan the QR code instead</button>
            </>
          )}
        </div>
      </form>
    );
  }

  const titles = {
    1: ['Forgot password', 'Enter your email and we’ll send you a code.'],
    2: ['Enter the code', 'We sent a 6-digit code to your email.'],
    3: ['New password', 'Choose a password you don’t use anywhere else.'],
  }[step];

  return (
    <form className="pair-card2 lg-card" onSubmit={submitReset}>
      <button type="button" className="lg-back" onClick={step === 1 ? leaveReset : () => setStep(step - 1)}
        aria-label="Back"><Back width={18} height={18} /></button>
      <h3>{titles[0]}</h3>
      <div className="csub">{titles[1]}</div>

      {err && <div className="lg-err"><Alert width={15} height={15} />{err}</div>}
      {note && <div className="lg-note"><Check width={15} height={15} />{note}</div>}

      {step === 1 && (
        <Field icon={Mail} label="Email address" type="email" autoComplete="username"
          placeholder="you@example.com" value={rEmail} onChange={(e) => setREmail(e.target.value)} />
      )}

      {step === 2 && (
        <label className="lg-field">
          <span className="lg-label">6-digit code</span>
          <input className="lg-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
            placeholder="123456" value={rCode}
            onChange={(e) => setRCode(e.target.value.replace(/\D/g, ''))} />
        </label>
      )}

      {step === 3 && (
        <>
          <Field icon={KeyRound} label="New password" type={show ? 'text' : 'password'}
            autoComplete="new-password" placeholder="New password" value={pw1}
            onChange={(e) => setPw1(e.target.value)} hint={eye} />
          <Field icon={KeyRound} label="Confirm new password" type={show ? 'text' : 'password'}
            autoComplete="new-password" placeholder="Repeat it" value={pw2}
            onChange={(e) => setPw2(e.target.value)} />
          <Rules pw={pw1} confirm={pw2} />
        </>
      )}

      <button className="lg-go" type="submit" disabled={busy}>
        {busy ? 'Working…' : step === 3 ? 'Reset password' : 'Continue'}
      </button>

      <div className="lg-alt">
        <button type="button" className="lg-link" onClick={leaveReset}>Back to log in</button>
      </div>
    </form>
  );
}
